import { useEffect, useRef, useState } from "react";
import { ArrowUp, Sparkles } from "lucide-react";
import { Eyebrow, Reveal } from "./shared";

const AUDIENCES = ["Job Seekers", "Employers", "Students", "Travelers"];

const GREETING = {
    role: "assistant",
    text: "Hello, I'm Afrinexa AI. Soon I'll help you navigate international jobs, recruitment, study-abroad pathways and global mobility services.",
};

const PLACEHOLDER_REPLY = {
    role: "assistant",
    text: "Thank you for your question. Afrinexa AI is being prepared for launch — for now, our team is ready to help you directly through the contact form or WhatsApp below.",
};

export const AfrinexaAI = () => {
    const [messages, setMessages] = useState([GREETING]);
    const [input, setInput] = useState("");
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const send = (e) => {
        e.preventDefault();
        const text = input.trim();
        if (!text) return;
        setMessages((prev) => [...prev, { role: "user", text }, PLACEHOLDER_REPLY]);
        setInput("");
    };

    const focusChat = () => {
        inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        inputRef.current?.focus({ preventScroll: true });
    };

    return (
        <section
            id="afrinexa-ai"
            data-testid="afrinexa-ai-section"
            className="bg-forest-deep py-24 text-ivory lg:py-32"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
                    <Reveal className="min-w-0">
                        <Eyebrow dark>Coming Soon</Eyebrow>
                        <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
                            Meet Afrinexa AI
                        </h2>
                        <p className="mt-6 max-w-lg text-base leading-relaxed text-ivory/70">
                            An intelligent assistant designed to help you navigate international jobs,
                            recruitment, study-abroad pathways and global mobility services.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {AUDIENCES.map((audience) => (
                                <span
                                    key={audience}
                                    data-testid={`ai-audience-${audience.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="border border-ivory/20 px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] text-ivory/75"
                                >
                                    {audience}
                                </span>
                            ))}
                        </div>
                        <button
                            type="button"
                            data-testid="ask-afrinexa-ai-btn"
                            onClick={focusChat}
                            className="mt-10 inline-flex items-center gap-2 bg-ivory px-8 py-4 text-sm font-medium text-ink transition-colors hover:bg-champagne"
                        >
                            <Sparkles size={16} strokeWidth={1.5} />
                            Ask Afrinexa AI
                        </button>
                    </Reveal>

                    <Reveal delay={0.15} className="min-w-0">
                        <div
                            data-testid="ai-chat-mock"
                            className="border border-ivory/15 bg-ivory/[0.06] p-6 backdrop-blur-md sm:p-8"
                        >
                            <div className="flex items-center justify-between border-b border-ivory/10 pb-5">
                                <div className="flex items-center gap-3">
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-champagne opacity-60" />
                                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-champagne" />
                                    </span>
                                    <span className="font-serif text-lg font-medium tracking-tight">
                                        Afrinexa AI
                                    </span>
                                </div>
                                <span className="border border-champagne/40 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.22em] text-champagne">
                                    Preview
                                </span>
                            </div>

                            <div
                                ref={scrollRef}
                                data-testid="ai-chat-messages"
                                className="mt-6 flex max-h-72 min-h-56 flex-col gap-4 overflow-y-auto pr-1"
                            >
                                {messages.map((message, i) => (
                                    <div
                                        key={i}
                                        className={`max-w-[85%] break-words px-4 py-3 text-sm leading-relaxed ${
                                            message.role === "user"
                                                ? "self-end bg-champagne text-ink"
                                                : "self-start bg-ivory/10 text-ivory/85"
                                        }`}
                                    >
                                        {message.text}
                                    </div>
                                ))}
                            </div>

                            <form onSubmit={send} className="mt-6 flex gap-3">
                                <input
                                    ref={inputRef}
                                    data-testid="ai-chat-input"
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Ask about jobs, study abroad, visas…"
                                    className="min-w-0 flex-1 border border-ivory/15 bg-night/40 px-4 py-3 text-sm text-ivory outline-none transition-colors placeholder:text-ivory/35 focus:border-champagne"
                                />
                                <button
                                    type="submit"
                                    data-testid="ai-chat-send-btn"
                                    aria-label="Send message"
                                    className="flex items-center justify-center border border-ivory bg-ivory px-4 text-ink transition-colors hover:bg-champagne hover:border-champagne"
                                >
                                    <ArrowUp size={18} strokeWidth={1.5} />
                                </button>
                            </form>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
